/**
 * CAPTCHA Widget Component
 *
 * Wrapper for Cloudflare Turnstile human verification.
 * Provides accessible fallback and dev mode bypass.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 *
 * Implementation notes:
 * - Uses @marsidev/react-turnstile package (or similar)
 * - In dev mode (no site key), auto-passes with dev token
 * - Provides accessible fallback for users who cannot complete CAPTCHA
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { cn } from '@/utils/cn';
import { config } from '@/config';

/**
 * Turnstile script state management
 */
interface TurnstileState {
  loaded: boolean;
  error: boolean;
}

export interface CaptchaWidgetProps {
  /** Callback when verification succeeds with token */
  onVerify: (token: string) => void;
  /** Callback when verification fails */
  onError?: () => void;
  /** Callback when token expires */
  onExpire?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Disable the widget */
  disabled?: boolean;
}

// Turnstile site key from runtime/build-time config
const TURNSTILE_SITE_KEY = config.turnstileSiteKey;

// Dev mode token (backend should accept this when CAPTCHA is disabled)
const DEV_MODE_TOKEN = 'dev-mode-token';

/**
 * CAPTCHA Widget Component
 *
 * Renders Cloudflare Turnstile widget for human verification.
 * Auto-passes in development when no site key is configured.
 */
export default function CaptchaWidget({
  onVerify,
  onError,
  onExpire,
  className,
  disabled = false,
}: CaptchaWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [turnstileState, setTurnstileState] = useState<TurnstileState>({
    loaded: false,
    error: false,
  });
  const [showFallback, setShowFallback] = useState(false);

  // Cleanup widget on unmount
  const cleanupWidget = useCallback(() => {
    if (
      widgetIdRef.current &&
      typeof window !== 'undefined' &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).turnstile
    ) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).turnstile.remove(widgetIdRef.current);
      } catch {
        // Ignore cleanup errors
      }
      widgetIdRef.current = null;
    }
  }, []);

  // Render widget when Turnstile loads
  const renderWidget = useCallback(() => {
    if (
      !containerRef.current ||
      disabled ||
      widgetIdRef.current ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      !(window as any).turnstile
    ) {
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      widgetIdRef.current = (window as any).turnstile.render(
        containerRef.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          callback: onVerify,
          'error-callback': () => {
            onError?.();
            // Show fallback after error
            setShowFallback(true);
          },
          'expired-callback': onExpire,
          theme: 'light',
          size: 'normal',
          // Accessibility: Show challenge only when needed
          appearance: 'interaction-only',
        }
      );
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[CaptchaWidget] Failed to render Turnstile:', err);
      }
      setTurnstileState((s) => ({ ...s, error: true }));
      setShowFallback(true);
    }
  }, [onVerify, onError, onExpire, disabled]);

  // Load Turnstile script
  useEffect(() => {
    // Dev mode: auto-pass with dev token
    if (!TURNSTILE_SITE_KEY) {
      onVerify(DEV_MODE_TOKEN);
      return;
    }

    // Check if already loaded
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).turnstile) {
      setTurnstileState({ loaded: true, error: false });
      return;
    }

    // Check if script already in DOM
    const existingScript = document.querySelector(
      'script[src*="turnstile"]'
    );
    if (existingScript) {
      // Wait for it to load
      existingScript.addEventListener('load', () => {
        setTurnstileState({ loaded: true, error: false });
      });
      return;
    }

    // Load Turnstile script
    const script = document.createElement('script');
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setTurnstileState({ loaded: true, error: false });
    };

    script.onerror = () => {
      if (import.meta.env.DEV) {
        console.error('[CaptchaWidget] Failed to load Turnstile script');
      }
      setTurnstileState({ loaded: false, error: true });
      setShowFallback(true);
    };

    document.head.appendChild(script);

    return () => {
      cleanupWidget();
    };
  }, [onVerify, cleanupWidget]);

  // Render widget when script loads
  useEffect(() => {
    if (turnstileState.loaded && !disabled) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(renderWidget, 100);
      return () => clearTimeout(timer);
    }
  }, [turnstileState.loaded, disabled, renderWidget]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupWidget();
  }, [cleanupWidget]);

  // Dev mode - render nothing (auto-passed)
  if (!TURNSTILE_SITE_KEY) {
    return null;
  }

  // Show accessible fallback
  if (showFallback || turnstileState.error) {
    return (
      <div className={cn('p-4 border border-yellow-200 bg-yellow-50 rounded-lg', className)}>
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm text-yellow-800">
              Having trouble with verification?
            </p>
            <p className="mt-1 text-sm text-yellow-700">
              Please try refreshing the page. If the issue persists,{' '}
              <a
                href="/contact"
                className="font-medium underline hover:text-yellow-800"
              >
                contact support
              </a>{' '}
              for assistance.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowFallback(false);
                setTurnstileState({ loaded: true, error: false });
                cleanupWidget();
                // Retry rendering after a brief delay
                setTimeout(renderWidget, 200);
              }}
              className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex justify-center', className)}>
      <div
        ref={containerRef}
        id="turnstile-container"
        className={cn(
          'min-h-[65px]', // Turnstile widget height
          disabled && 'opacity-50 pointer-events-none'
        )}
        aria-label="Human verification"
      />
    </div>
  );
}
